/**
 * Everything the bulk bar needs, in one hook: what is selected, what a run
 * would do, running it, and reporting it.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueueElement, QueuePoolSummary } from '@trafficpulse/shared';

import { useToast } from '@/providers/ToastProvider';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from '../../../../hooks/useFlowOptimizerQueue';
import type { QueueSegment } from '../../queue-filters';
import { bulkActionsFor, bulkOpFor, type BulkActionDef } from '../bulk-actions';
import { planBulkPromote } from '../bulk-plan';
import { useBulkRunner } from '../useBulkRunner';
import { useQueueSelection } from '../useQueueSelection';
import { performBulkOp } from './perform';

export interface BulkQueueActionsInput {
  campaignId: string;
  provider: QueueProvider;
  segment: QueueSegment;
  /** Rows currently on screen, in draw order. */
  rows: QueueElement[];
  pools: QueuePoolSummary[];
  /** Part of the selection reset key — changing layer drops the selection. */
  layer: string;
  /** Also part of the reset key. Without it a selection made under "Disabled"
   *  survives a switch to "Active" and the next bulk action would run on rows
   *  the user can no longer see. */
  status?: string;
}

/** Demote takes elements off the tracker; the other ops are bank-only. */
const TRACKER_ACTIONS = new Set<QueueSegment>(['testing']);

export function useBulkQueueActions(input: BulkQueueActionsInput) {
  const { campaignId, provider, segment, rows, pools, layer, status } = input;
  const qc = useQueryClient();
  const toast = useToast();
  const selection = useQueueSelection(rows, `${segment}:${layer}:${status ?? 'all'}`);
  const runner = useBulkRunner();
  const [pending, setPending] = useState<BulkActionDef | null>(null);
  /** An explicit item set that IGNORES the checkbox selection — used by the
   *  "move all disabled" button, which acts on what the filter found rather
   *  than on what the user ticked. Null ⇒ the selection drives the run. */
  const [override, setOverride] = useState<QueueElement[] | null>(null);

  const plan = useMemo(
    () =>
      pending?.id === 'promote'
        ? planBulkPromote(selection.selected, pools)
        : null,
    [pending, selection.selected, pools],
  );

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) });
    void qc.invalidateQueries({
      queryKey: queueKeys.rotationHistory(provider, campaignId),
    });
    void qc.invalidateQueries({
      queryKey: [provider, 'flow-optimizer', 'queue-events', campaignId],
    });
  }, [qc, provider, campaignId]);

  const confirm = useCallback(async () => {
    const action = pending;
    const op = action && bulkOpFor(segment, action.id);
    if (!action || !op) return;
    const items = override ?? (plan ? plan.fitting : selection.selected);
    setPending(null);
    const results = await runner.run(items, (el) =>
      performBulkOp(op, campaignId, provider, el),
    );
    refresh();
    setOverride(null);
    const ok = results.filter((r) => r.ok).length;
    const failed = results.length - ok;
    // Only the checkbox selection is cleared — an override never owned one.
    if (ok > 0 && !override) selection.clear();
    if (failed === 0 && ok > 0) toast.success(`${ok} ${action.past}`);
    else if (ok === 0 && failed > 0) toast.error(`Nothing was ${action.past}`);
    else if (failed > 0) toast.warning(`${ok} ${action.past}, ${failed} refused`);
  }, [
    pending,
    segment,
    plan,
    override,
    selection,
    runner,
    campaignId,
    provider,
    refresh,
    toast,
  ]);

  return {
    selection,
    runner,
    pending,
    plan,
    actions: bulkActionsFor(segment),
    writesTracker: TRACKER_ACTIONS.has(segment),
    /** The rows the confirm dialog is about — the override when one is set. */
    targets: override ?? selection.selected,
    start: setPending,
    /** Open the confirm for an explicit set of rows, ignoring the selection. */
    startFor: (action: BulkActionDef, items: QueueElement[]) => {
      setOverride(items);
      setPending(action);
    },
    cancelDialog: () => {
      setOverride(null);
      setPending(null);
    },
    confirm,
  };
}
